import {useMemo, lazy} from 'react';
import {Navigate, Route, Routes} from 'react-router-dom';

const GuidebookView = lazy(() =>
  import('pages/GuidebookView/GuidebookView').then(m => ({default: m.GuidebookView})),
);
const GuidebookHtmlView = lazy(() =>
  import('pages/GuidebookHtmlView/GuidebookHtmlView').then(m => ({
    default: m.GuidebookHtmlView,
  })),
);
const GuidebookPdfView = lazy(() =>
  import('pages/GuidebookPdfView/GuidebookPdfView').then(m => ({
    default: m.GuidebookPdfView,
  })),
);
const HousingGuidebooks = lazy(() =>
  import('pages/HousingGuidebooksView/HousingGuidebooksView').then(m => ({
    default: m.HousingGuidebooksView,
  })),
);
const HousingGuidebookView = lazy(() =>
  import('pages/HousingGuidebooksView/GuidebookView').then(m => ({
    default: m.GuidebookView,
  })),
);
const HousingGuidebooksLayout = lazy(() =>
  import('pages/HousingGuidebooksView/HousingGuidebooksLayout').then(m => ({
    default: m.HousingGuidebooksLayout,
  })),
);
const Payments = lazy(() =>
  import('pages/Payments/Payments').then(m => ({default: m.Payments})),
);
const CheckoutView = lazy(() =>
  import('pages/Checkout/CheckoutView').then(m => ({default: m.default})),
);
const OfferView = lazy(() =>
  import('pages/OfferView/OfferView').then(m => ({default: m.OfferView})),
);
const OfferSlugRedirectView = lazy(() =>
  import('pages/OfferSlugRedirectView').then(m => ({
    default: m.OfferSlugRedirectView,
  })),
);
import {
  useInitGuestTaxes,
  usePath,
  useInitPropertyProtection,
  useSetPreviousPage,
  useGuestGroup,
} from 'hooks';
import {useSchema} from 'context/guest';
import {useReservation} from 'context/reservation';
import {useCustomTheme} from 'context/customTheme';
import {useSummary} from 'context/summary';
import {useCheckoutSettings} from 'pages/Checkout/hooks/useCheckoutSettings';
import {PATHS, IS_SDK_MODE, addPersonalDataFormRoute} from 'routes/constants';
import AddPersonalDataForm from 'pages/AddPersonalDataForm';
import FAQView from './pages/FAQView';
import DepositView from './pages/DepositView';
import HomeView from 'pages/HomeView';
import RemoteAccessView from 'pages/RemoteAccess';
import RootLayout from './components/RootLayout';
import SearchReservation from 'pages/SearchReservation';
import ConnectionErrorsHandler from 'components/ConnectionErrorsHandler';
import {PaymentFormView, PaymentsView} from 'pages/Payments';
import {BeforeYouLeaveView, RateYourStayView, ThanksView} from './pages/Checkout';
import MRZView, {MRZScanView, MRZStartView} from './pages/MRZView';
import {OnlyGuestFormRoutes} from 'routes/OnlyGuestFormRoutes';
import {OnlyGuestRegistrationRoutes} from 'routes/OnlyGuestRegistrationRoutes';
import {TaxesExemptionView, TaxesTouristView} from './pages/Taxes';
import {BookExperienceView} from 'pages/BookExperienceView';
import {EnterContactEmailView} from 'pages/EnterContactEmailView';
import {OfferListView} from 'pages/OfferListView';
import {WhoIsRegistering} from 'pages/WhoIsRegistering';
import {OnlyIVRoutes} from 'routes/OnlyIVRoutes';
import {OnlyQRIVOnPropertyRoutes} from 'routes/OnlyQRIVOnPropertyRoutes';
import {OnlyPropertyLinkRoutes} from 'routes/OnlyPropertyLinkRoutes';
import {OAuthCallback} from 'pages/Auth/views/OAuthCallback';
import {RemoteKeys} from 'components/RemoteAccess/RemoteKeys';

import {getIVRoutes} from 'routes/IVRoutes';
import {
  BookingCodeView,
  CheckInDateView,
  DirectReservationView,
  NewReservationView,
} from 'pages/SearchReservation/Views';
import {
  LoginView,
  WelcomeView,
  Onboarding,
  NumberOfGuestsStep,
  ReservationTypeStep,
  WhoIsComingStep,
  ConfirmGuestLeaderStep,
} from 'pages/Auth';
import {ManageEsimView} from 'pages/esim/ManageEsimView';
import {ChoosePlanView} from 'pages/esim/ChoosePlanView';
import {MyEsimsView} from 'pages/esim/MyEsimsView';
import {WelcomeScreenWrapper} from 'components/Kiosko';
import {VerifyOnHousingView} from 'pages/VerifyOnHousing';
import {FindReservationsByNameView} from 'pages/FindReservationsByName';
import {WelcomeLayout} from 'components/WelcomeLayout';
import {GuestRegistrationView} from 'pages/GuestRegistrationView';
import {ViewLoader} from 'pages/Auth/components/ViewLoader';
import {SDK_MODE, getFeatureFlags} from '@chekin/core';

const isGuidebookPdfHtmlEnabled = getFeatureFlags('IT_5024_GUIDEBOOKS_PDF_HTML_SUPPORT');

function AppRouter() {
  useInitGuestTaxes();
  useInitPropertyProtection();
  useSetPreviousPage();
  const {isTemplateInitialized} = useCustomTheme();
  const {getFullPath} = usePath();
  const {housing, isLoading: isReservationLoading} = useReservation();
  const {isLoading: isGuestsGroupLoading} = useGuestGroup();
  const {isInstantCheckInEnabled, isLoading} = useSummary();
  const {isGuestSchemaInitialLoading} = useSchema();
  const {isCheckoutEnabled} = useCheckoutSettings();

  const isLoadingMinimalData =
    !isTemplateInitialized ||
    isLoading ||
    isReservationLoading ||
    isGuestsGroupLoading ||
    isGuestSchemaInitialLoading;

  const routes = useMemo(() => {
    if (IS_SDK_MODE && window.ChekinProSettings?.mode) {
      const mode = window.ChekinProSettings.mode;

      if (!isTemplateInitialized) {
        return <Route path="*" index element={<ViewLoader />} />;
      }

      if (mode === SDK_MODE.onlyGuestForm) {
        return <Route path="*" index element={<OnlyGuestFormRoutes />} />;
      }

      if (mode === SDK_MODE.onlyIV) {
        return <Route path="*" index element={<OnlyIVRoutes />} />;
      }

      if (mode === SDK_MODE.propertyLink) {
        return <Route path="*" index element={<OnlyPropertyLinkRoutes />} />;
      }

      if (mode === SDK_MODE.guestRegistrationOnly) {
        return <Route path="*" index element={<OnlyGuestRegistrationRoutes />} />;
      }

      if (mode === SDK_MODE.qrIVOnProperty) {
        return <Route path="*" index element={<OnlyQRIVOnPropertyRoutes />} />;
      }
    }

    return (
      <>
        <Route element={<RootLayout isLoading={isLoadingMinimalData} />}>
          <Route index element={<HomeView />} />
          <Route path={PATHS.guestRegistration} element={<GuestRegistrationView />} />

          {isInstantCheckInEnabled && (
            <>
              <Route path={PATHS.whoIsRegistering} element={<WhoIsRegistering />} />
              <Route path={PATHS.auth.root}>
                <Route index element={<LoginView />} />
                <Route path={PATHS.auth.createAccount} element={<LoginView />} />
                <Route path={PATHS.auth.login} element={<LoginView />} />
                <Route path={PATHS.auth.welcome} element={<WelcomeView />} />
              </Route>
              <Route path={PATHS.onboarding.root} element={<Onboarding />}>
                <Route index element={<Navigate to="number-of-guest" replace />} />
                <Route
                  path={PATHS.onboarding.reservationType}
                  element={<ReservationTypeStep disabled={false} />}
                />
                <Route
                  path={PATHS.onboarding.numberOfGuests}
                  element={<NumberOfGuestsStep />}
                />
                <Route
                  path={PATHS.onboarding.whoIsComing}
                  element={<WhoIsComingStep />}
                />
                <Route
                  path={PATHS.onboarding.confirmLeaderGuest}
                  element={<ConfirmGuestLeaderStep />}
                />
              </Route>
            </>
          )}
          {addPersonalDataFormRoute.map((route, index) => {
            return <Route key={index} path={route} element={<AddPersonalDataForm />} />;
          })}
          <Route path={PATHS.deposits} element={<DepositView />}>
            <Route path=":id" element={<DepositView />} />
          </Route>
          <Route path={PATHS.payments.main} element={<Payments />}>
            <Route index path="*" element={<PaymentsView />} />
            <Route path={PATHS.payments.form} element={<PaymentFormView />} />
          </Route>
          {isCheckoutEnabled && (
            <Route path={PATHS.checkout.main} element={<CheckoutView />}>
              <Route index element={<BeforeYouLeaveView />} />
              <Route
                path={PATHS.checkout.beforeYouLeave}
                element={<BeforeYouLeaveView />}
              />
              <Route path={PATHS.checkout.rateYourStay} element={<RateYourStayView />} />
              <Route path={PATHS.checkout.thanks} element={<ThanksView />} />
            </Route>
          )}
          <Route path={PATHS.taxes.main}>
            <Route index element={<TaxesTouristView />} />
            <Route path={PATHS.taxes.exemptions} element={<TaxesExemptionView />}>
              <Route path=":id" element={<TaxesExemptionView />} />
            </Route>
          </Route>

          {getIVRoutes({
            isLivenessDetectionEnabled: housing?.is_liveness_detection_enabled,
          })}
          <Route path={PATHS.documentMRZ} element={<MRZView />}>
            <Route index element={<MRZStartView />} />
            <Route path={':uploadType'} element={<MRZScanView />} />
          </Route>
          <Route path={PATHS.remoteAccess} element={<RemoteAccessView />} />
          <Route path={PATHS.remoteKeys} element={<RemoteKeys />} />
          <Route path={PATHS.guidebook} element={<GuidebookView />}>
            <Route path=":id" element={<GuidebookView />} />
          </Route>
          {isGuidebookPdfHtmlEnabled && (
            <>
              <Route path={PATHS.guidebookHtml}>
                <Route path=":id" element={<GuidebookHtmlView />} />
              </Route>
              <Route path={PATHS.guidebookPdf}>
                <Route path=":id" element={<GuidebookPdfView />} />
              </Route>
            </>
          )}
          <Route path={PATHS.housingGuidebooks} element={<HousingGuidebooksLayout />}>
            <Route index element={<HousingGuidebooks />} />
            <Route path=":id" element={<HousingGuidebookView />} />
          </Route>
          <Route path={PATHS.faq} element={<FAQView />} />
          <Route path={PATHS.searchReservation.main}>
            <Route index element={<SearchReservation />} />
          </Route>
          <Route
            path={PATHS.searchReservation.welcomeScreen}
            element={<WelcomeScreenWrapper />}
          />
          <Route
            path={PATHS.searchReservation.bookingCode}
            element={<BookingCodeView />}
          />
          <Route
            path={PATHS.searchReservation.checkingDate}
            element={<CheckInDateView />}
          />
          <Route
            path={PATHS.searchReservation.newReservation}
            element={<NewReservationView />}
          />
          <Route path={PATHS.esim.manage} element={<ManageEsimView />} />
          <Route path={PATHS.esim.choosePlan} element={<ChoosePlanView />} />
          <Route path={PATHS.esim.my}>
            <Route index path="*" element={<MyEsimsView />} />
          </Route>
          <Route path={PATHS.recommendations.main}>
            <Route index element={<OfferListView />} />
            <Route path={PATHS.recommendations.details.main}>
              <Route
                path={PATHS.recommendations.details.bookExperience}
                element={<BookExperienceView />}
              />
              <Route
                path={PATHS.recommendations.details.confirmEmail}
                element={<EnterContactEmailView />}
              />
              <Route index element={<OfferView />} />
            </Route>
            <Route path={PATHS.remoteAccess} element={<RemoteAccessView />} />
            <Route path={PATHS.remoteKeys} element={<RemoteKeys />} />
            <Route path={PATHS.guidebook} element={<GuidebookView />}>
              <Route path=":id" element={<GuidebookView />} />
            </Route>
            {isGuidebookPdfHtmlEnabled && (
              <>
                <Route path={PATHS.guidebookHtml}>
                  <Route path=":id" element={<GuidebookHtmlView />} />
                </Route>
                <Route path={PATHS.guidebookPdf}>
                  <Route path=":id" element={<GuidebookPdfView />} />
                </Route>
              </>
            )}
            <Route path={PATHS.faq} element={<FAQView />} />
            <Route path={PATHS.searchReservation.main}>
              <Route index element={<SearchReservation />} />
            </Route>
            <Route
              path={PATHS.searchReservation.bookingCode}
              element={<BookingCodeView />}
            />
            <Route
              path={PATHS.searchReservation.checkingDate}
              element={<CheckInDateView />}
            />
            <Route
              path={PATHS.searchReservation.newReservation}
              element={<NewReservationView />}
            />
            <Route path={PATHS.recommendations.main}>
              <Route index element={<OfferListView />} />
              <Route path={PATHS.recommendations.details.main}>
                <Route
                  path={PATHS.recommendations.details.bookExperience}
                  element={<BookExperienceView />}
                />
                <Route
                  path={PATHS.recommendations.details.confirmEmail}
                  element={<EnterContactEmailView />}
                />
                <Route index element={<OfferView />} />
              </Route>
            </Route>
            <Route path="*" element={<Navigate to={getFullPath(PATHS.home)} />} />
          </Route>
        </Route>

        <Route element={<WelcomeLayout />}>
          <Route path={PATHS.verifyOnHousing} element={<VerifyOnHousingView />} />
          <Route
            path={PATHS.findReservationsByName}
            element={<FindReservationsByNameView />}
          />
        </Route>
        <Route
          path={PATHS.searchReservation.directReservation}
          element={<DirectReservationView />}
        />
      </>
    );
  }, [
    getFullPath,
    isTemplateInitialized,
    housing?.is_liveness_detection_enabled,
    isInstantCheckInEnabled,
    isLoadingMinimalData,
    isCheckoutEnabled,
  ]);

  return (
    <Routes>
      <Route path={PATHS.oauth.google} element={<OAuthCallback />} />
      <Route path="/" element={<ConnectionErrorsHandler />}>
        {!IS_SDK_MODE && (
          <Route
            path="/:token/:slug"
            element={<RootLayout isLoading={isLoadingMinimalData} />}
          >
            <Route index element={<OfferSlugRedirectView />} />
          </Route>
        )}
        {IS_SDK_MODE ? (
          <Route path="/*">{routes}</Route>
        ) : (
          <Route path={PATHS.home}>{routes}</Route>
        )}
      </Route>
    </Routes>
  );
}
export default AppRouter;
